import ActionMenu from "@components/ActionMenu/ActionMenu";
import CustomizedButton from "@components/ButtonConfirm/ButtonConfirm";
import BankInfoQuote from "@components/createQuote/BankInfoQuote/BankInfoQuote";
import CompanyClientDetails from "@components/createQuote/companyClientDetails/CompanyClientDetails";
import QuoteDetails from "@components/createQuote/QuoteDetails/QuoteDetails";
import QuoteTable from "@components/createQuote/QuoteTable/QuoteTable";
import TotalQuotePrices from "@components/createQuote/TotalQuotePrices/TotalQuotePrices";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { PATHS } from "config/paths";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetCurrentBankQuery } from "store/bank/bank.api";
import { useGetClientsQuery } from "store/client/clientApi";
import { useGetOffersQuery } from "store/offer/offerApi";
import {
  useCreateQuoteDraftMutation,
  useCreateQuoteMutation,
  useGetNextQuoteNumberQuery,
} from "store/quote/quoteApi";
import { useGetUserByIdQuery } from "store/user/userApi";
import "./CreateQuoteForm.scss";
import { quoteSchema } from "./schemaQuote";

const CreateQuoteForm = ({
  isUpdate,
  initialValues = {},
  onSave,
  onCancel,
}) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const { data } = useGetUserByIdQuery(currentUser?._id, {
    skip: !currentUser?._id,
  });
  const [createQuoteDraft, { isLoading: isLoadingdraft }] =
    useCreateQuoteDraftMutation();
  console.log("🚀 ~ CreateQuoteForm ~ data:", data);

  const companyId = currentUser?.companyId?._id;
  const [errorMsg, setErrorMsg] = useState("");
  const [numDevis, setNumDevis] = useState("");

  const [note, setNote] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [executionConditions, setExecutionConditions] = useState("");

  useEffect(() => {
    setNote(
      initialValues.note
        ? initialValues?.note
        : data?.data?.companyId?.note || ""
    );
    setPaymentTerms(
      initialValues.paymentTerms
        ? initialValues?.paymentTerms
        : data?.data?.companyId?.payment_method ||
            "50% à la commande, solde à la livraison"
    );
    setExecutionConditions(
      initialValues.executionConditions
        ? initialValues?.executionConditions
        : data?.data?.companyId?.executionConditions ||
            "Les prestations seront réalisées dans les 15 jours suivant l'accord."
    );
  }, [data, initialValues]);

  const [quoteLines, setQuoteLines] = useState(
    initialValues?.items || [
      {
        offerId: "",
        quantity: 1,
        unitPrice: 0,

        tva: 0,
        reduction: 0,
        description: "",
      },
    ]
  );
  const { register, handleSubmit, watch, setValue, getValues, reset } = useForm(
    {
      resolver: yupResolver(quoteSchema),
      defaultValues: {
        items: isUpdate ? initialValues?.items : quoteLines,
        idClient: isUpdate ? initialValues?.idClient?._id : "",
        idUserClient: isUpdate ? initialValues?.idUserClient?._id : "",
      },
    }
  );
  const { data: nextQuoteNumber } = useGetNextQuoteNumberQuery(companyId);
  useEffect(() => {
    setNumDevis(nextQuoteNumber?.data?.numeroQuote);
  }, [nextQuoteNumber]);

  const { data: clients = { data: [] } } = useGetClientsQuery(companyId);
  const [createQuote] = useCreateQuoteMutation();

  const { data: currentBankData, isLoading: isLoadingBankData } =
    useGetCurrentBankQuery(currentUser?.companyId?._id);
  const { data: offersData } = useGetOffersQuery(companyId);
  const [selectedClientId, setSelectedClientId] = useState(
    isUpdate ? initialValues?.idClient : ""
  );
  const [selectedClientData, setSelectedClientData] = useState({});

  const [totals, setTotals] = useState({
    subtotal: 0,
    discountAmount: 0,
    tva: 0,
    ttc: 0,
  });
  useEffect(() => {
    if (isUpdate && initialValues) {
      const resetData = {
        ...initialValues,
        idClient: initialValues?.idClient?._id || "",
        idUserClient: initialValues?.idUserClient?._id || "",
        issueDate: initialValues?.issueDate?.substring(0, 10) || "", // format YYYY-MM-DD
        startDate: initialValues?.startDate?.substring(0, 10) || "",
        items:
          initialValues?.items?.map((item) => ({
            offerId: item.offerId || "",
            quantity: item.quantity || 1,
            price: item.unitPrice || 0,

            tva: item.tva || 0,
            reduction: item.reduction || 0,
            description: item.designation || "", // si tu veux afficher une description
          })) || [],
      };

      reset(resetData);
      setQuoteLines(resetData.items);
      setSelectedClientId(initialValues?.idClient?._id || "");
    }
  }, [initialValues, isUpdate, reset]);

  useEffect(() => {
    const computedTotals = calculateTotals(quoteLines);
    setTotals(computedTotals);
  }, [quoteLines]);

  const handleLineChange = (index, field, value) => {
    const updatedLines = [...quoteLines];
    updatedLines[index][field] = value;
    setQuoteLines(updatedLines);
  };

  const handleItemChange = (index, value) => {
    const updatedLines = [...quoteLines];

    updatedLines[index] = {
      ...value,
    };
    setQuoteLines(updatedLines);
  };

  const handleAddLine = () => {
    setQuoteLines([
      ...quoteLines,
      {
        offerId: "",

        quantity: 1,
        price: 0,
        reduction: 0,
        tva: 0,
      },
    ]);
  };

  const handleRemoveLine = (index) => {
    const updated = [...quoteLines];
    updated.splice(index, 1);
    setQuoteLines(updated);
  };

  const handleClick = async () => {
    const formData = getValues(); // all form data

    if (quoteLines.length === 0) {
      toast.error(t("quote.error.no_line"));
      return;
    }

    const invalidLines = quoteLines
      .map((line, index) => {
        const errors = [];
        if (!line.price || Number(line.price) <= 0)
          errors.push(t("quote.unit_price"));
        if (!line.quantity || Number(line.quantity) <= 0)
          errors.push(t("quote.quantity"));
        return errors.length > 0 ? { index: index + 1, errors } : null;
      })
      .filter(Boolean);

    if (invalidLines.length !== 0) {
      setErrorMsg(t("quote.error_add_offers"));
    }

    const totals = calculateTotals(quoteLines);

    const dataToDisplay = {
      ...formData,
      items: quoteLines.map((item) => ({
        designation: item.description,
        quantity: Number(item.quantity),
        unitPrice: Number(item.price),
        reduction: Number(item.reduction),
        tva: Number(item.tva),
        idOffer: item?._id || null,
      })),
      totalHT: parseFloat(totals.subtotal.toFixed(2)),
      remise: parseFloat(totals.discountAmount.toFixed(2)),
      totalTTC: parseFloat(totals.ttc.toFixed(2)),
      numeroDevis: numDevis,
      companyId: isUpdate ? formData?.companyId?._id : companyId,
      idClient: formData?.idClient,
      idUserClient: formData?.idUserClient,
      paymentTerms: paymentTerms
        ? paymentTerms
        : "50% à la commande, solde à la livraison",
      paymentDeadline: "30jours",
      executionConditions: executionConditions
        ? executionConditions
        : "Les prestations seront réalisées dans les 15 jours suivant l'accord.",
      note: note ? note : "Devis reçu avant l’exécution des travaux",
      rib: currentBankData?.data?.rib,
      idbank: currentBankData?.data?._id,
    };
    try {
      console.log("dataToDisplay", dataToDisplay);

      const res = isUpdate
        ? await onSave(dataToDisplay)
        : await createQuote(dataToDisplay).unwrap();

      if (!isUpdate) {
        toast.success(t("quote.success_created"));
      }

      setTimeout(() => {
        navigate(PATHS.QUOTES.LIST);
      }, 1500);
    } catch (err) {
      const messages = err?.data?.message || [];

      // 🧠 Dictionnaire messages clairs
      const errorMap = {
        "idClient must be a mongodb id": "Ajoutez la société.",
        "idUserClient must be a mongodb id": "Ajoutez le client.",

        "items.0.designation should not be empty":
          "Ajoutez la description du produit.",
        "items.0.unitPrice must be a number conforming to the specified constraints":
          "Ajoutez le prix du produit.",
        "items.0.idOffer should not be empty": "Ajoutez les offres.",
      };

      const readableErrors = messages.map((msg) => errorMap[msg] || msg);

      // ✅ Afficher toutes les erreurs dans un seul toast
      toast.error(readableErrors.join("\n"));
    }

    console.log("📦 Données actuelles du formulaire :", dataToDisplay);
  };

  const calculateTotals = (lines) => {
    let subtotal = 0;
    let discountAmount = 0;
    let totalTva = 0;

    lines.forEach((line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.price || line.unitPrice || 0);
      const reduction = parseFloat(line.reduction || 0);
      const tva = parseFloat(line.tva || 0);

      const lineTotalHT = quantity * unitPrice;
      const lineDiscount = (lineTotalHT * reduction) / 100;
      const lineAfterDiscount = lineTotalHT - lineDiscount;
      const lineTvaAmount = (lineAfterDiscount * tva) / 100;

      subtotal += lineTotalHT;
      discountAmount += lineDiscount;
      totalTva += lineTvaAmount;
    });

    const ttc = subtotal - discountAmount + totalTva;

    return {
      subtotal,
      discountAmount,
      tva: totalTva,
      ttc,
    };
  };

  useEffect(() => {
    const totals = calculateTotals(quoteLines);
    setValue("totalHT", totals.subtotal);
    setValue("discount", totals.discountAmount);
    setValue("tvaRate", totals.tva);
    setValue("totalTTC", totals.ttc);
    setValue("items", quoteLines);
  }, [quoteLines, setValue]);
  useEffect(() => {
    setValue("idClient", selectedClientId);
  }, [selectedClientId, setValue]);

  const handleClientChange = (clientId) => {
    setSelectedClientId(clientId);
    const selected = clients.data.find((c) => c._id === clientId);
    if (selected) {
      setSelectedClientData(selected);
    }
  };

  const idUserClient = watch("idUserClient");
  console.log("currentBankData?.data", currentBankData?.data);
  const handleSaveDraft = async () => {
    const formData = getValues(); // all form data

    if (quoteLines.length === 0) {
      toast.error(t("quote.error.no_line"));
      return;
    }

    const invalidLines = quoteLines
      .map((line, index) => {
        const errors = [];
        if (!line.price || Number(line.price) <= 0)
          errors.push(t("quote.unit_price"));
        if (!line.quantity || Number(line.quantity) <= 0)
          errors.push(t("quote.quantity"));
        return errors.length > 0 ? { index: index + 1, errors } : null;
      })
      .filter(Boolean);

    if (invalidLines.length !== 0) {
      setErrorMsg(t("quote.error_add_offers"));
    }

    const totals = calculateTotals(quoteLines);

    const dataToDisplay = {
      ...formData,
      items: quoteLines.map((item) => ({
        designation: item.description,
        quantity: Number(item.quantity),
        unitPrice: Number(item.price),
        reduction: Number(item.reduction),
        tva: Number(item.tva),
        idOffer: item?._id || null,
      })),
      totalHT: parseFloat(totals.subtotal.toFixed(2)),
      remise: parseFloat(totals.discountAmount.toFixed(2)),
      totalTTC: parseFloat(totals.ttc.toFixed(2)),
      numeroDevis: numDevis,
      companyId: isUpdate ? formData?.companyId?._id : companyId,
      idClient: formData?.idClient,
      idUserClient: formData?.idUserClient,
      paymentTerms: paymentTerms,
      paymentDeadline: "30jours",
      executionConditions: executionConditions,
      note: note ? note : "Devis reçu avant l’exécution des travaux",
      rib: currentBankData?.data?.rib,
      idbank: currentBankData?.data?._id,
    };

    try {
      const res = await createQuoteDraft(dataToDisplay).unwrap();

      setTimeout(() => {
        navigate(PATHS.QUOTES.LIST);
      }, 1000);
    } catch (err) {
      toast.error(
        err?.data ? err?.data?.message[0] : "Problem when creating devis"
      );
    }

    console.log("📦 Données actuelles du formulaire :", dataToDisplay);
  };
  return (
    <>
      <form onSubmit={handleSubmit(handleClick)} className="quote-form">
        <QuoteDetails
          titleSection={t("quote.details_quote")}
          numeroDevis={isUpdate ? initialValues?.numeroDevis : numDevis}
          register={register}
          initialValues={initialValues}
        />

        <CompanyClientDetails
          companyData={currentUser?.companyId}
          selectedClientId={
            initialValues?.idClient?._id
              ? initialValues?.idClient?._id
              : selectedClientId
          }
          selectedClientData={selectedClientData}
          clientsData={clients.data}
          handleClientChange={handleClientChange}
          register={register}
          idUserClient={
            initialValues?.idUserClient?._id
              ? initialValues.idUserClient?._id
              : idUserClient
          }
          initialValues={initialValues}
          isUpdate={isUpdate}
        />

        <Stack
          className="invoice-section"
          sx={{
            backgroundColor: "#f8fbff",
            padding: "1.5rem",
            borderRadius: "16px",
          }}>
          <div className="invoice-table-wrapper d-flex ">
            <QuoteTable
              lines={quoteLines}
              offersData={offersData?.data || []}
              handleLineChange={handleLineChange}
              handleAddLine={handleAddLine}
              handleRemoveLine={handleRemoveLine}
              handleDragEnd={() => {}}
              isUpdate={isUpdate}
              initialValues={initialValues}
              handleItemChange={handleItemChange}
            />

            <ActionMenu
              addOffer={handleAddLine}
              deleteOffer={handleRemoveLine}
              isWithDeleteColumn={false}
            />
          </div>
          {errorMsg && <p className="error-message">❌{errorMsg}</p>}
        </Stack>
        <TotalQuotePrices
          totals={totals}
          isUpdate={isUpdate}
          initialValues={initialValues}
        />

        {/* <BankInfoQuote
        isLoadingBankData={isLoadingBankData}
        note={note ? note : "Devis reçu avant l’exécution des travaux"}
        paymentTerms={
          paymentTerms
            ? paymentTerms
            : "50% à la commande, solde à la livraison"
        }
        s
        paymentInfo={currentBankData?.data}
      /> */}
        <BankInfoQuote
          isLoadingBankData={isLoadingBankData}
          note={note ? note : "Devis reçu avant l’exécution des travaux"}
          setNote={setNote}
          paymentTerms={
            paymentTerms
              ? paymentTerms
              : "50% à la commande, solde à la livraison"
          }
          setPaymentTerms={setPaymentTerms}
          executionConditions={
            executionConditions
              ? executionConditions
              : "Les prestations seront réalisées dans les 15 jours suivant l'accord."
          }
          setExecutionConditions={setExecutionConditions}
          paymentInfo={currentBankData?.data}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-end",
          }}>
          <CustomizedButton
            creatingText={t("invoice.creating")}
            finalizeText={t("quote.finalize")}
            stylee={{ minWidth: "200px" }}
            handleCreate={handleClick}
          />
        </div>
      </form>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "flex-end",
          marginTop: "1rem",
        }}>
        <CustomizedButton
          finalizeText={t("invoice.save_draft")}
          bgColor={"#CACACA"}
          handleCreate={handleSaveDraft}
          stylee={{ minWidth: "200px" }}
        />
      </div>
    </>
  );
};

export default CreateQuoteForm;
